import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ZafaraFullLogo } from "@/components/landing/Logo";

export default function AdminLogin() {
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!ready) return <div className="min-h-screen bg-[#FBFAF6]" />;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Renseignez email et mot de passe.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenue !");
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Identifiants invalides ou serveur indisponible.";
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex flex-col">
      <header className="p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#16241B]/70 hover:text-[#0E7A3B] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour au site
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <ZafaraFullLogo />
          </div>

          <div className="rounded-3xl border border-[#EDE7D9] bg-white p-8 md:p-10 shadow-[0_20px_60px_rgba(14,122,59,0.08)]">
            <p className="font-utility text-[10px] uppercase tracking-[0.25em] text-[#0E7A3B]">
              Espace privé
            </p>
            <h1 className="mt-3 font-display text-3xl md:text-4xl text-[#16241B] tracking-tight leading-tight">
              Connexion à l'administration.
            </h1>
            <p className="mt-3 text-sm text-[#16241B]/60">
              Réservée à l'équipe Whilsco-Digit.
            </p>

            <form onSubmit={submit} noValidate className="mt-8 space-y-5">
              <label className="block">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">
                  Email
                </span>
                <div className="mt-2 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#16241B]/40" />
                  <input
                    data-testid="admin-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] pl-9 pr-4 py-3 text-base text-[#16241B] focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors"
                    placeholder="vous@whilsco.fr"
                  />
                </div>
              </label>

              <label className="block">
                <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">
                  Mot de passe
                </span>
                <div className="mt-2 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#16241B]/40" />
                  <input
                    data-testid="admin-login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] pl-9 pr-4 py-3 text-base text-[#16241B] focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] focus:border-[#0E7A3B] transition-colors"
                    placeholder="••••••••"
                  />
                </div>
              </label>

              <button
                data-testid="admin-login-submit"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0E7A3B] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(14,122,59,0.35)] hover:bg-[#F5A800] hover:text-[#16241B] disabled:opacity-60 transition-all duration-300"
              >
                <LogIn className="h-4 w-4" />
                {loading ? "Connexion…" : "Se connecter"}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs text-[#16241B]/50">
            Zafara est une marque de Whilsco-Digit.
          </p>
        </div>
      </main>
    </div>
  );
}
